import { useMutation } from "@apollo/client/react";
import { PlusOutlined } from "@ant-design/icons";
import { App, Button } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AddRecipeIngredientDocument,
  type GenericIngredientFragment,
  type IngredientFragment,
  IngredientType,
} from "../../../gql/generated/graphqlSchema.tsx";
import { useSaving } from "../../../hooks/useSaving.tsx";
import { genericIngredientDisplayName } from "../../../utils/genericIngredient.ts";
import { CreateIngredientModal } from "../../ingredient/CreateIngredientModal.tsx";
import { useRecipe } from "../RecipeContext.tsx";
import { IngredientSearchModal, type SearchItem } from "./IngredientSearchModal.tsx";

export const AddIngredientModal = () => {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const { recipe, setRecipe, units } = useRecipe();
  const { trackSaving } = useSaving();
  const [addRecipeIngredient] = useMutation(AddRecipeIngredientDocument);
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createFromGeneric, setCreateFromGeneric] = useState<GenericIngredientFragment | null>(null);

  const gramsUnitId = units.find((u) => u.code === "g")?.id;

  const handleSelect = (item: SearchItem) => {
    if (!gramsUnitId) return;
    setOpen(false);
    trackSaving(async () => {
      const r = await addRecipeIngredient({
        variables: {
          recipeVersionId: recipe.versionId,
          input: {
            ingredientType: item.ingredientType,
            ingredientId: item.ingredientType === IngredientType.Ingredient ? item.id : undefined,
            subRecipeId: item.ingredientType === IngredientType.Recipe ? item.id : undefined,
            quantity: "100",
            unitId: gramsUnitId,
          },
        },
      });
      setRecipe(r.data?.addRecipeIngredient?.refreshedRecipe);
      message.success(t("recipes.addIngredientDone"));
    });
  };

  const handleCreated = (ingredient: IngredientFragment) => {
    setCreateFromGeneric(null);
    handleSelect({
      id: ingredient.id,
      name: ingredient.name,
      nutritionProfile: ingredient.nutritionProfile,
      ingredientType: IngredientType.Ingredient,
    });
  };

  return (
    <>
      <Button color="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)} variant="solid">
        {t("recipes.addIngredient")}
      </Button>
      <IngredientSearchModal
        actionIcon={<PlusOutlined />}
        actionLabel={t("recipes.addIngredient")}
        genericActionLabel={t("recipes.createAndAdd")}
        onClose={() => setOpen(false)}
        onCreateFromGeneric={(gi) => setCreateFromGeneric(gi)}
        onCreateIngredient={() => {
          setOpen(false);
          setCreateOpen(true);
        }}
        onSelect={handleSelect}
        open={open}
        title={t("recipes.addIngredientTitle")}
      />
      <CreateIngredientModal
        genericIngredient={createFromGeneric ?? undefined}
        initialName={createFromGeneric != null ? genericIngredientDisplayName(createFromGeneric, i18n.language) : ""}
        onClose={() => {
          setCreateFromGeneric(null);
          setCreateOpen(false);
        }}
        onCreated={handleCreated}
        open={createFromGeneric != null || createOpen}
      />
    </>
  );
};
